import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { api, errMsg } from "../../lib/api";
import { useLang } from "../../lib/i18n";
import { Field, useBusy } from "../common";

const COLS = ["missing", "same", "changed", "added", "overwritten", "conflicts"];
const RCOLS = ["remove", "copy", "removed", "copied"];

function Report({ report, t }) {
  const rows = Object.entries(report || {});
  const cols = rows.length && "copy" in rows[0][1] ? RCOLS : COLS;
  if (!rows.length) return <p className="text-sm text-slate-500" data-testid="restore-report-empty">{t("Nothing to restore for this selection.", "Nada que restaurar para esta selección.")}</p>;
  return (
    <div className="overflow-x-auto border rounded-lg" data-testid="restore-report">
      <table className="w-full text-xs mono">
        <thead className="bg-slate-50"><tr><th className="text-left p-2">{t("Data", "Datos")}</th>{cols.map((c) => <th key={c} className="p-2 text-right">{c}</th>)}</tr></thead>
        <tbody>{rows.map(([n, r]) => <tr key={n} className="border-t" data-testid={`restore-row-${n}`}><td className="p-2">{n}</td>{cols.map((c) => <td key={c} className="p-2 text-right">{r[c]}</td>)}</tr>)}</tbody>
      </table>
    </div>
  );
}

export function RestoreDialog({ backup, onClose }) {
  const { t } = useLang();
  const [orgs, setOrgs] = useState([]);
  const [f, setF] = useState({ org_id: "", overwrite: false, replace: false, confirm: "" });
  const [res, setRes] = useState(null);
  const [busy, run] = useBusy();
  useEffect(() => { api.get("/platform/companies").then((r) => setOrgs(r.data)).catch(() => {}); }, []);
  const go = (apply) => run(async () => {
    try {
      const { data } = await api.post(`/platform/backups/${backup.id}/restore`, { org_id: f.org_id || null, apply, overwrite: f.overwrite, replace: f.replace && !f.org_id, confirm: f.confirm });
      setRes(data);
      if (apply) toast.success(t("Restore finished. A safety backup was taken first.", "Restauración terminada. Antes se hizo una copia de seguridad."));
    } catch (e) { toast.error(errMsg(e)); }
  });
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="restore-dialog">
        <DialogHeader>
          <DialogTitle>{t("Restore backup", "Restaurar copia")} · {backup.created_at?.slice(0, 16).replace("T", " ")}</DialogTitle>
          <DialogDescription>{t("Preview first. By default only missing records are added back; current data is never deleted and companies are never mixed.", "Primero vea la vista previa. Por defecto solo se agregan registros que faltan; nunca se borran datos actuales ni se mezclan empresas.")}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t("Company", "Empresa")}>
            <select className="inp" value={f.org_id} onChange={(e) => { setF({ ...f, org_id: e.target.value }); setRes(null); }} data-testid="restore-company-select">
              <option value="">{t("All companies", "Todas las empresas")}</option>
              {orgs.map((o) => <option key={o.id} value={o.id}>{o.name}</option>)}
            </select>
          </Field>
          <label className="flex items-center gap-2 text-sm"><input type="checkbox" checked={f.overwrite} onChange={(e) => setF({ ...f, overwrite: e.target.checked })} data-testid="restore-overwrite-checkbox" />{t("Also overwrite records that changed since the backup (same company only)", "También sobrescribir registros que cambiaron desde la copia (solo misma empresa)")}</label>
          {!f.org_id && <label className="flex items-start gap-2 text-sm text-red-700 font-semibold"><input type="checkbox" className="mt-1" checked={f.replace} onChange={(e) => { setF({ ...f, replace: e.target.checked }); setRes(null); }} data-testid="restore-replace-checkbox" />{t("REPLACE EVERYTHING: make this database an exact copy of the backup. Records not in the backup are removed. A safety backup of the current data is taken first so you can undo it.", "REEMPLAZAR TODO: esta base de datos queda idéntica a la copia. Se quitan los registros que no están en la copia. Antes se guarda una copia de seguridad de los datos actuales para poder deshacerlo.")}</label>}
          <button className="btn-outline" disabled={busy} onClick={() => go(false)} data-testid="restore-preview-btn">{t("Preview restore", "Vista previa")}</button>
          {res && <Report report={res.report} t={t} />}
          {res && !res.applied && (
            <div className="grid gap-2 border-t pt-3">
              <Field label={t("Type RESTORE to confirm", "Escriba RESTORE para confirmar")}><input className="inp" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="restore-confirm-input" /></Field>
              <button className="btn-danger" disabled={busy || f.confirm !== "RESTORE"} onClick={() => go(true)} data-testid="restore-apply-btn">{t("Apply restore", "Aplicar restauración")}</button>
            </div>
          )}
          {res?.applied && <p className="text-sm text-emerald-700 font-semibold" data-testid="restore-applied-msg">{t("Restore applied.", "Restauración aplicada.")}</p>}
        </div>
      </DialogContent>
    </Dialog>
  );
}
